'use client';

import { motion, useReducedMotion, useScroll, useTransform } from 'framer-motion';
import type { ReactNode } from 'react';
import { useRef } from 'react';
import VideoLazyLoad from '@/app/components/shared/VideoLazyLoad';
import { useDeferredMediaRender } from '../useDeferredMediaRender';

export interface FeatureCardMedia {
  videoSrc: string;
  poster: string;
  mobilePoster?: string;
  alt: string;
}

interface FeatureCardProps {
  eyebrow?: string;
  /** 卡片左下角标题 */
  title?: ReactNode;
  /** 标题下的一句描述 */
  description?: ReactNode;
  /** 自定义左下角文案内容；传入时替代默认的 eyebrow/title/description。 */
  children?: ReactNode;
  media: FeatureCardMedia;
}

/**
 * 第 2 屏：一张居中大圆角卡片，媒体铺满，文案压在左下角。
 *
 * 深色底、大留白，卡片进入视口时轻微上浮 + 淡入。卡内底部加黑色渐变保证文字清晰。
 * 滚动视差：媒体略微放大后随滚动进度上下位移，形成「卡片里的画面在动」的层次感
 * （媒体放大到 120% 保证位移时不露出卡片边缘）。
 * 降级：reduced-motion 关闭进场 / 视差动画；媒体懒挂载（进入视口才加载视频）。
 */
export const FeatureCard = ({ eyebrow, title, description, children, media }: FeatureCardProps) => {
  const prefersReducedMotion = useReducedMotion();
  const { ref: gateRef, shouldRenderMedia } = useDeferredMediaRender<HTMLDivElement>('300px 0px');
  const sectionRef = useRef<HTMLDivElement>(null);

  // 追踪该段从「底部进入视口」到「顶部离开视口」的滚动进度 [0,1]，驱动媒体视差位移
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ['start end', 'end start'] });
  const mediaY = useTransform(scrollYProgress, [0, 1], ['-8%', '8%']);
  // 卡片本身在向上滚出时逐渐缩小（后半段行程才收缩，进场阶段保持原尺寸）
  const cardScale = useTransform(scrollYProgress, [0.5, 1], [1, 0.92]);
  // 文案层反向轻微下移，与媒体形成速度差 → 视差
  const contentY = useTransform(scrollYProgress, [0, 1], ['6%', '-6%']);

  return (
    <section ref={sectionRef} className="w-full bg-black p-10 sm:p-4">
      <motion.div
        ref={gateRef}
        className="relative mx-auto aspect-[16/9] w-full max-w-[1600px] overflow-hidden rounded-[48px] border border-white/[0.16] bg-system-white06 sm:aspect-[3/4] sm:rounded-24 md:aspect-[3/4]"
        initial={prefersReducedMotion ? undefined : { opacity: 0, y: 48 }}
        whileInView={prefersReducedMotion ? undefined : { opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
        style={prefersReducedMotion ? undefined : { scale: cardScale }}
      >
        {/* 铺满媒体（放大 + 随滚动位移形成视差） */}
        <motion.div
          className="absolute inset-0 h-full w-full"
          style={prefersReducedMotion ? undefined : { y: mediaY, scale: 1.2 }}
        >
          {shouldRenderMedia ? (
            <VideoLazyLoad
              poster={media.poster}
              mobilePoster={media.mobilePoster}
              videoSrc={media.videoSrc}
              className="!absolute inset-0 h-full w-full"
              videoClassName="object-cover object-center"
              preload="none"
              alt={media.alt}
            />
          ) : (
            <div className="absolute inset-0 h-full w-full bg-system-white06" />
          )}
        </motion.div>

        {/* 左→右黑色蒙版：左 64% → 右 0 */}
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-black/[0.32] to-transparent" />

        {/* 底部渐变，保证左下角文字清晰 */}
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/35 via-black/5 to-transparent" />

        {/* 左侧文案，与视频垂直居中对齐；随滚动轻微反向位移形成视差 */}
        <motion.div
          className="absolute inset-0 flex flex-col items-start justify-center p-12 text-left sm:p-5 md:p-6"
          style={prefersReducedMotion ? undefined : { y: contentY }}
        >
          {children ?? (
            <>
              {eyebrow && (
                <span className="mb-4 block text-sm text-white/70 uppercase tracking-[0.3em] sm:text-xs md:mb-3">
                  {eyebrow}
                </span>
              )}
              <h2 className="font-semibold text-[3.5rem] text-white leading-[1.05] tracking-[-0.02em] sm:text-[1.75rem] md:text-[2.25rem] lg:text-[2.75rem]">
                {title}
              </h2>
              {description && (
                <p className="mt-5 max-w-[56ch] text-white/75 text-xl leading-relaxed sm:text-sm md:mt-4 lg:text-lg">
                  {description}
                </p>
              )}
            </>
          )}
        </motion.div>
      </motion.div>
    </section>
  );
};
